import { PERMISSIONS } from '../constants/permissions';
import { PROFILE } from '../constants/profile';
import { COUNTRIES } from '../constants/countries';
import { isAccessible } from '../utils/isAllowedPermission';
import { getProfileType, isBillsEnabled } from '../utils/localStorageUtils';
import { getKeysPermissions } from '../views/pages/inner_layout/ApiKeys/utils';
import { INNER_ROUTES } from '../constants/inner-routes';

import Home from '../views/pages/inner_layout/Home';
import Balance from '../views/pages/inner_layout/BalanceAndTransfers/pages/Balance';
import Transfers from '../views/pages/inner_layout/BalanceAndTransfers/pages/Transfers';
import SingleTransfer from '../views/pages/inner_layout/BalanceAndTransfers/pages/Transfers/SingleTransfer';
import { Transactions } from '../views/pages/inner_layout/Transactions';
import { SingleTransaction } from '../views/pages/inner_layout/Transactions/SingleTransaction';
import ChargeBacks from '../views/pages/inner_layout/ChargeBacks';
import SingleChargeBacks from '../views/pages/inner_layout/ChargeBacks/SingleChargeBacks';
import { PayMe } from '../views/pages/inner_layout/PayMe';
import { PayMeSettings } from '../views/pages/inner_layout/PayMe/Settings';
import { Orders } from '../views/pages/inner_layout/Orders';
import { SingleOrder } from '../views/pages/inner_layout/Orders/SingleOrder';
import { QuickLinks } from '../views/pages/inner_layout/QuickLinks';
import { SingleQuickLink } from '../views/pages/inner_layout/QuickLinks/SingleQuickLink';
import Campaigns from '../views/pages/inner_layout/Reach/pages/Campaigns';
import SingleCampaign from '../views/pages/inner_layout/Reach/pages/Campaigns/SingleCampaign';
import CampaignsAnalysis from '../views/pages/inner_layout/Reach/pages/CampaignsAnalysis';
import Bundles from '../views/pages/inner_layout/Reach/pages/Bundles';
import { Reports } from '../views/pages/inner_layout/Reports';
import RiskCenter from '../views/pages/inner_layout/RiskCenter';
import SingleRiskCenter from '../views/pages/inner_layout/RiskCenter/SingleRiskCenter';
import { Devices } from '../views/pages/inner_layout/Devices';
import { Branches } from '../views/pages/inner_layout/Branches';
import { Summary } from '../views/pages/inner_layout/Summary';
import { Settings } from '../views/pages/inner_layout/Settings';
import { ProfileInfo } from '../views/pages/inner_layout/ProfileDetails/pages/ProfileInfo';
import { ChangePassword } from '../views/pages/inner_layout/ProfileDetails/pages/ChangePassword';
import { BusinessProfile } from '../views/pages/inner_layout/BusinessProfile';
import { UserManagement } from '../views/pages/inner_layout/UserManagement';
import { Notifications } from '../views/pages/inner_layout/Notifications';
import { CheckoutCustomization } from '../views/pages/inner_layout/CheckoutCustomization';
import { Services } from '../views/pages/inner_layout/Services';
import { Payment_Integrations } from '../views/pages/inner_layout/PaymentIntegrations';
import { SinglePaymentIntegration } from '../views/pages/inner_layout/PaymentIntegrations/SingleIntegration';
import ApiKeys from '../views/pages/inner_layout/ApiKeys';
import { Iframes } from '../views/pages/inner_layout/Iframes';
import { Xero } from '../views/pages/inner_layout/Xero';
import { BulkPaymentLinks } from '../views/pages/inner_layout/PaymentLinks/BulkPaymentLink';
import { SinglePaymentLink } from '../views/pages/inner_layout/PaymentLinks/SinglePaymentLink';
import { Bills } from '../views/pages/inner_layout/Bills';
import { PayAds } from '../views/pages/inner_layout/Bills/PayAds';
import { SingleBill } from '../views/pages/inner_layout/Bills/SingleBill';
import { Disputes } from '../views/pages/inner_layout/Disputes';
import { SingleDispute } from '../views/pages/inner_layout/Disputes/SingleDispute';



// Route path constants live in a standalone module to avoid coupling them to
// the page-component graph imported below. Re-exported here for backward compat.
export { INNER_ROUTES };

const isStaff = () => getProfileType() === PROFILE.TYPE.MERCHANT_STAFF;
const isMerchant = () => getProfileType() === PROFILE.TYPE.MERCHANT;

export const innerLayoutRoutes = [
  {
    name: 'home',
    path: INNER_ROUTES.HOME,
    element: <Home />,
  },
  {
    name: "balance",
    path: INNER_ROUTES.BALANCE,
    element: <Balance />,
    specialCondition: () => isAccessible(PERMISSIONS.CAN_VIEW_TRANSFERS),
  },
  {
    name: "transfers",
    path: INNER_ROUTES.TRANSFERS,
    element: <Transfers />,
    specialCondition: () => isAccessible(PERMISSIONS.CAN_VIEW_TRANSFERS),
  },
  {
    name: "single_transfer",
    path: INNER_ROUTES.SINGLE_TRANSFER,
    element: <SingleTransfer />,
    specialCondition: () => isAccessible(PERMISSIONS.CAN_VIEW_TRANSFERS),
  },
  {
    name: 'transactions',
    path: INNER_ROUTES.TRANSACTIONS,
    element: <Transactions />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_TRANSACTIONS], exact: true },
  },
  {
    name: 'single_transaction',
    path: INNER_ROUTES.SINGLE_TRANSACTION,
    element: <SingleTransaction />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_TRANSACTIONS], exact: true },
  },
  {
    name: 'orders',
    path: INNER_ROUTES.ORDERS,
    element: <Orders />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_ORDERS], exact: true },
  },
  {
    name: 'single_order',
    path: INNER_ROUTES.SINGLE_ORDER,
    element: <SingleOrder />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_ORDERS], exact: true },
  },
  {
    name: 'chargeBacks',
    path: INNER_ROUTES.CHARGE_BACKS,
    element: <ChargeBacks />,
    permissions: { list: [PERMISSIONS.CAN_HANDLE_CHARGE_BACKS], exact: true },
  },
  {
    name: 'single_chargeBacks',
    path: INNER_ROUTES.SINGLE_CHARGE_BACK,
    element: <SingleChargeBacks />,
    permissions: { list: [PERMISSIONS.CAN_HANDLE_CHARGE_BACKS], exact: true },
  },
  {
    name: "quick_links",
    path: INNER_ROUTES.QUICK_LINKS,
    element: <QuickLinks />,
    specialCondition: () => isStaff() ? isAccessible(PERMISSIONS.CAN_VIEW_QUICK_LINKS) : true,
  },
  {
    name: 'single_quick_link',
    path: INNER_ROUTES.SINGLE_QUICK_LINK,
    element: <SingleQuickLink />,
    specialCondition: () => isStaff() ? isAccessible(PERMISSIONS.CAN_VIEW_QUICK_LINKS) : true,
  },
  {
    name: "payme",
    path: INNER_ROUTES.PAYME,
    element: <PayMe />,
    specialCondition: () => isStaff() ? isAccessible(PERMISSIONS.CAN_VIEW_PAYMENT_HANDLE) : true,
  },
  {
    name: "payme_single_transaction",
    path: INNER_ROUTES.PAYME_SINGLE_TRANSACTION,
    element: <SingleTransaction />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_TRANSACTIONS], exact: true },
  },
  {
    name: "payme_settings",
    path: INNER_ROUTES.PAYME_SETTINGS,
    element: <PayMeSettings />,
    specialCondition: () => isStaff() ? isAccessible(PERMISSIONS.CAN_CHANGE_PAYMENT_HANDLE) : true,
  },
  {
    name: "campaigns", path: INNER_ROUTES.CAMPAIGNS, element: <Campaigns />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_REACH], exact: true },
    allowedCountries: [COUNTRIES.EGY],
  },
  {
    name: "single_campaign", path: INNER_ROUTES.SINGLE_CAMPAIGN, element: <SingleCampaign />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_REACH], exact: true },
    allowedCountries: [COUNTRIES.EGY],
  },
  {
    name: "campaign_analysis", path: INNER_ROUTES.CAMPAIGN_ANALYSIS, element: <CampaignsAnalysis />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_REACH], exact: true },
    allowedCountries: [COUNTRIES.EGY],
  },
  {
    name: "bundles", path: INNER_ROUTES.BUNDLES, element: <Bundles />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_REACH], exact: true },
    allowedCountries: [COUNTRIES.EGY],
  },
  {
    name: 'reports',
    path: INNER_ROUTES.REPORTS,
    element: <Reports />,
    permissions: { list: [PERMISSIONS.CAN_DOWNLOAD_REPORTS], exact: true },
  },
  {
    name: 'risk_center',
    path: INNER_ROUTES.RISK_CENTER,
    element: <RiskCenter />,
    specialCondition: () => isMerchant()
  },
  {
    name: 'risk_center_item',
    path: INNER_ROUTES.SINGLE_RISK_ITEM,
    element: <SingleRiskCenter />,
    specialCondition: () => isMerchant()
  },
  {
    name: 'devices',
    path: INNER_ROUTES.DEVICES,
    element: <Devices />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_TERMINAL], exact: true },
    specialCondition: () => isMerchant()
  },
  {
    name: 'branches',
    path: INNER_ROUTES.BRANCHES,
    element: <Branches />,
    specialCondition: () => isMerchant()
  },
  {
    name: 'summary',
    path: INNER_ROUTES.SUMMARY,
    element: <Summary />,
    permissions: { list: [PERMISSIONS.CAN_EXPORT_TERMINAL], exact: true },
    specialCondition: () => isMerchant()
  },
  {
    name: 'settings',
    path: INNER_ROUTES.SETTINGS,
    element: <Settings />,
  },
  {
    name: 'profile_details',
    path: INNER_ROUTES.PROFILE_DETAILS,
    element: <ProfileInfo />,
  },
  {
    name: 'change_password',
    path: INNER_ROUTES.CHANGE_PASSWORD,
    element: <ChangePassword />,
  },
  {
    name: 'business_profile',
    path: INNER_ROUTES.BUSINESS_PROFILE,
    element: <BusinessProfile />,
  },
  {
    name: 'user_and_permissions',
    path: INNER_ROUTES.USER_AND_PERMISSIONS,
    element: <UserManagement />,
    permissions: {
      list: [PERMISSIONS.CAN_VIEW_STAFF],
      exact: true,
    },
  },
  {
    name: 'manage_roles',
    path: INNER_ROUTES.MANAGE_ROLES,
    element: <UserManagement />,
    permissions: {
      list: [PERMISSIONS.CAN_VIEW_STAFF, PERMISSIONS.CAN_CREATE_STAFF],
      exact: true,
    },
  },
  {
    name: 'create_new_role',
    path: INNER_ROUTES.CREATE_NEW_ROLE,
    element: <UserManagement />,
    permissions: {
      list: [
        PERMISSIONS.CAN_CREATE_STAFF,
        PERMISSIONS.CAN_VIEW_STAFF,
      ],
      exact: true,
    },
  },
  {
    name: 'role_details',
    path: INNER_ROUTES.ROLE_DETAILS,
    element: <UserManagement />,
    permissions: {
      list: [PERMISSIONS.CAN_VIEW_STAFF, PERMISSIONS.CAN_UPDATE_STAFF],
      exact: true,
    },
  },
  {
    name: 'notifications',
    path: INNER_ROUTES.NOTIFICATIONS,
    element: <Notifications />,
    specialCondition: () => !isStaff()
  },
  {
    name: 'checkout_customization',
    path: INNER_ROUTES.CHECKOUT_CUSTOMIZATION,
    element: <CheckoutCustomization />,
    specialCondition: () => isMerchant()
  },
  {
    name: 'services',
    path: INNER_ROUTES.SERVICES,
    element: <Services />,
    specialCondition: () => isMerchant(),
  },
  {
    name: 'payment_integrations',
    path: INNER_ROUTES.PAYMENT_INTEGRATIONS,
    element: <Payment_Integrations />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_PAYMENT_INTEGRATIONS], exact: true },
  },
  {
    name: 'single_payment_integration',
    path: INNER_ROUTES.SINGLE_PAYMENT_INTEGRATION,
    element: <SinglePaymentIntegration />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_PAYMENT_INTEGRATIONS], exact: true },
  },
  {
    name: 'api_keys',
    path: INNER_ROUTES.API_KEYS,
    element: <ApiKeys />,
    specialCondition: () => !(Object.values(getKeysPermissions()).every(permission => !permission) && isStaff())
  },
  {
    name: 'iframes',
    path: INNER_ROUTES.IFRAMES,
    element: <Iframes />,
    permissions: { list: [PERMISSIONS.CAN_VIEW_IFRAMES], exact: true },
  },
  {
    name: 'xero',
    path: INNER_ROUTES.XERO,
    element: <Xero />,
  },
  {
    name: 'bulk_payment_links',
    path: INNER_ROUTES.BULK_PAYMENT_LINKS,
    element: <BulkPaymentLinks />,
    specialCondition: () => (isStaff() ? isAccessible(PERMISSIONS.CAN_CREATE_QUICK_LINKS) : true),
  },
  {
    name: 'single_payment_link',
    path: INNER_ROUTES.SINGLE_PAYMENT_LINK,
    element: <SinglePaymentLink />,
    specialCondition: () => (isStaff() ? isAccessible(PERMISSIONS.CAN_CREATE_QUICK_LINKS) : true),
  },
  {
    name: 'bills',
    path: INNER_ROUTES.BILLS,
    element: <Bills />,
    specialCondition: () => isBillsEnabled()
  },
  {
    name: 'single_bill',
    path: INNER_ROUTES.SINGLE_BILL,
    element: <SingleBill />,
    specialCondition: () => isBillsEnabled()
  },
  {
    name: 'pay_ads',
    path: INNER_ROUTES.PAY_ADS,
    element: <PayAds />,
    specialCondition: () => isBillsEnabled()
  },
  {
    name: 'disputes',
    path: INNER_ROUTES.DISPUTES,
    element: <Disputes />,
    permissions: {
      list: [PERMISSIONS.CAN_MANAGE_DISPUTES],
      exact: true,
    },
  },
  {
    name: 'single_dispute',
    path: INNER_ROUTES.SINGLE_DISPUTE,
    element: <SingleDispute />,
    permissions: {
      list: [PERMISSIONS.CAN_MANAGE_DISPUTES],
      exact: true,
    },
  }
];

export const creationModuleRoutes = [
  INNER_ROUTES.BULK_PAYMENT_LINKS,
  INNER_ROUTES.SINGLE_PAYMENT_LINK
];